import { fetchAuth } from "../../../shared/api/ApiService"
import { ICancelLessonRequest, ICompleteLessonRequest, ICreateExtraLessonRequest, IDeleteLessonRequest, IGetCalendarFilterOptionsRequest,
    IGetCalendarFilterOptionsResponse, IGetDayCalendarRequest, IGetDayCalendarResponse, IGetMonthCalendarRequest,
    IGetMonthCalendarResponse, IGetLessonResponse, ILessonResponse, IListLessonHistoryResponse, ICalendarDay, IRescheduleLessonRequest,
    IRescheduleLessonResponse, IReopenCancelledLessonRequest,
} from "../model/types"

type TCalendarDayApi = ICalendarDay & {
    scheduled_count?: number;
    completed_count?: number;
    cancelled_count?: number;
    rescheduled_count?: number;
    trial_count?: number;
    has_orange_trial?: boolean;
    is_available?: boolean;
}

const mapCalendarDay = (day: TCalendarDayApi): ICalendarDay => ({
    date: day.date,
    scheduledCount: day.scheduledCount ?? day.scheduled_count ?? 0,
    completedCount: day.completedCount ?? day.completed_count ?? 0,
    cancelledCount: day.cancelledCount ?? day.cancelled_count ?? 0,
    rescheduledCount: day.rescheduledCount ?? day.rescheduled_count ?? 0,
    trialCount: day.trialCount ?? day.trial_count ?? 0,
    hasOrangeTrial: day.hasOrangeTrial ?? day.has_orange_trial ?? false,
    isAvailable: day.isAvailable ?? day.is_available ?? true,
})

class CalendarService {
    async getCalendarFilterOptions(
        data: IGetCalendarFilterOptionsRequest,
        signal?: AbortSignal,
    ): Promise<IGetCalendarFilterOptionsResponse> {
        const res = await fetchAuth(
            `${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/calendar/filter-options`,
            {
                method: 'POST',
                body: JSON.stringify(data),
                signal,
            }
        )

        const response = await res.json()
        const normalizedTags = response.tags || []

        return {
            ...response,
            tgAccounts: response.tgAccounts || [],
            tutors: response.tutors || [],
            students: response.students || [],
            tags: normalizedTags,
        }
    }

    async getMonthCalendar(data: IGetMonthCalendarRequest): Promise<IGetMonthCalendarResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/calendar/month`, {
            method: 'POST',
            body: JSON.stringify(data),
        })

        const response = await res.json()

        return {
            ...response,
            days: (response.days || []).map(mapCalendarDay),
        }
    }

    async getDayCalendar(data: IGetDayCalendarRequest): Promise<IGetDayCalendarResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/calendar/day`, {
            method: 'POST',
            body: JSON.stringify(data),
        })

        return await res.json()
    }

    async getLesson(lessonId: string): Promise<IGetLessonResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/calendar/lessons/${lessonId}`)

        return await res.json()
    }

    async createExtraLesson(data: ICreateExtraLessonRequest): Promise<ILessonResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/calendar/lessons/extra`, {
            method: 'POST',
            body: JSON.stringify(data),
        })

        return await res.json()
    }

    async completeLesson(data: ICompleteLessonRequest): Promise<ILessonResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/calendar/lessons/${data.lessonId}/complete`, {
            method: 'POST',
            body: JSON.stringify(data),
        })

        return await res.json()
    }

    async cancelLesson(data: ICancelLessonRequest): Promise<ILessonResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/calendar/lessons/${data.lessonId}/cancel`, {
            method: 'POST',
            body: JSON.stringify(data),
        })

        return await res.json()
    }

    async reopenCancelledLesson(data: IReopenCancelledLessonRequest): Promise<ILessonResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/calendar/lessons/${data.lessonId}/reopen-cancelled`, {
            method: 'POST',
            body: JSON.stringify(data),
        })

        return await res.json()
    }

    async deleteLesson(data: IDeleteLessonRequest): Promise<void> {
        await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/calendar/lessons/${data.lessonId}/delete`, {
            method: 'POST',
            body: JSON.stringify(data),
        })
    }

    async rescheduleLesson(data: IRescheduleLessonRequest): Promise<IRescheduleLessonResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/calendar/lessons/${data.lessonId}/reschedule`, {
            method: 'POST',
            body: JSON.stringify(data),
        })

        return await res.json()
    }

    async getLessonHistory(lessonId: string): Promise<IListLessonHistoryResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/calendar/lessons/${lessonId}/history`)

        return await res.json()
    }
}

export const calendarService = new CalendarService()
